/**
 * WISE SYNC PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Sync transactions from Wise API
 * Import payments into the system
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  RefreshCw,
  ArrowDownCircle,
  Calendar,
  DollarSign,
  TrendingUp,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  ExternalLink,
  AlertCircle,
  ArrowRightCircle,
  Wallet
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { apiClient } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { SkeletonStatCard, SkeletonCard } from '../../components/ui/Skeleton'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface WiseBalance {
  id: number
  currency: string
  amount: {
    value: number
    currency: string
  }
  reservedAmount?: {
    value: number
    currency: string
  }
}

interface WiseTransaction {
  date: string
  type: string
  amount: {
    value: number
    currency: string
  }
  runningBalance?: {
    value: number
    currency: string
  }
  referenceNumber: string
  details: {
    description?: string
    senderName?: string
    type?: string
  }
}

interface ExchangeRate {
  source: string
  target: string
  rate: number
  timestamp: string
}

export function WiseSyncPage() {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [balances, setBalances] = useState<WiseBalance[]>([])
  const [transactions, setTransactions] = useState<WiseTransaction[]>([])
  const [exchangeRate, setExchangeRate] = useState<ExchangeRate | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ imported: number; skipped: number } | null>(null)

  // Date range for fetching transactions
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setMonth(d.getMonth() - 1)
    return d.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [selectedCurrency, setSelectedCurrency] = useState('USD')

  // Selected transactions for import
  const [selectedTxns, setSelectedTxns] = useState<Set<string>>(new Set())
  const [importing, setImporting] = useState(false)

  // Check admin access
  const isAdmin = user?.type === 'Admin'

  // Fetch Wise balance
  const fetchBalance = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await apiClient.get('/wise/balance')

      if (data.success) {
        setBalances(data.data)
      } else {
        setError(data.error || 'Failed to fetch balance')
      }
    } catch (err: any) {
      setError(err.message || 'Network error fetching balance')
    } finally {
      setLoading(false)
      setInitialLoading(false)
    }
  }

  // Fetch exchange rate
  const fetchExchangeRate = async () => {
    try {
      const data = await apiClient.get('/wise/exchange-rate?source=USD&target=PKR')

      if (data.success) {
        setExchangeRate(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch exchange rate:', err)
    }
  }

  // Fetch Wise transactions
  const fetchTransactions = async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await apiClient.get(`/wise/transactions?start=${startDate}&end=${endDate}&currency=${selectedCurrency}`)

      if (data.success) {
        setTransactions(data.data.transactions || [])
        // Auto-select all credit transactions
        const credits = (data.data.transactions || [])
          .filter((t: WiseTransaction) => t.type === 'CREDIT' && t.amount.value > 0)
          .map((t: WiseTransaction) => t.referenceNumber)
        setSelectedTxns(new Set(credits))
      } else {
        setError(data.error || 'Failed to fetch transactions')
      }
    } catch (err: any) {
      setError(err.message || 'Network error fetching transactions')
    } finally {
      setLoading(false)
    }
  }

  // Import selected transactions
  const importTransactions = async () => {
    setImporting(true)
    setError(null)
    setImportResult(null)

    try {
      const data = await apiClient.post('/wise/import-transactions', {
        startDate,
        endDate,
        currency: selectedCurrency
      })

      if (data.success) {
        setImportResult({
          imported: data.data.imported,
          skipped: data.data.skipped
        })
        setSelectedTxns(new Set())
      } else {
        setError(data.error || 'Failed to import transactions')
      }
    } catch (err) {
      setError('Network error importing transactions')
    } finally {
      setImporting(false)
    }
  }

  // Toggle transaction selection
  const toggleTransaction = (refNumber: string) => {
    const newSet = new Set(selectedTxns)
    if (newSet.has(refNumber)) {
      newSet.delete(refNumber)
    } else {
      newSet.add(refNumber)
    }
    setSelectedTxns(newSet)
  }

  // Select all credit transactions
  const selectAllCredits = () => {
    const credits = transactions
      .filter(t => t.type === 'CREDIT' && t.amount.value > 0)
      .map(t => t.referenceNumber)
    setSelectedTxns(new Set(credits))
  }

  // Clear selection
  const clearSelection = () => {
    setSelectedTxns(new Set())
  }

  // Initial fetch
  useEffect(() => {
    if (isAdmin && user) {
      fetchBalance()
      fetchExchangeRate()
    } else {
      setInitialLoading(false)
    }
  }, [user, isAdmin])

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount)
  }

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Access denied state
  if (!isAdmin) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <ContentCard>
          <div className="text-center py-16">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={springConfig}
              className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-red/10 to-red/5 flex items-center justify-center"
            >
              <AlertCircle className="w-10 h-10 text-red" />
            </motion.div>
            <h2 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
              Access Denied
            </h2>
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
              Only administrators can access Wise sync functionality.
            </p>
          </div>
        </ContentCard>
      </div>
    )
  }

  // Loading state
  if (initialLoading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-36 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-56 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="h-10 w-32 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
        </div>
        <StatCardGrid columns={4}>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </StatCardGrid>
        <SkeletonCard className="h-48" />
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Wise Sync
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Import transactions from your Wise account
          </p>
        </div>
        <a
          href="https://wise.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2.5 bg-[#9FE870] hover:bg-[#8DD65F] text-black font-semibold rounded-xl transition-colors text-body"
        >
          <ExternalLink className="w-4 h-4" />
          Open Wise
        </a>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import Success Alert */}
      <AnimatePresence>
        {importResult && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-green/20 bg-green/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-green/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green" />
                </div>
                <div className="flex-1">
                  <p className="text-green font-medium">
                    Successfully imported {importResult.imported} transactions
                    {importResult.skipped > 0 && `, skipped ${importResult.skipped} duplicates`}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setImportResult(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Balance Cards */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="USD Balance"
            value={balances.find(b => b.currency === 'USD')?.amount.value
              ? formatCurrency(balances.find(b => b.currency === 'USD')!.amount.value, 'USD')
              : '$0.00'}
            icon={DollarSign}
            color="green"
            badge={<Badge variant="green">Wise</Badge>}
          />
          <StatCard
            title="PKR Balance"
            value={balances.find(b => b.currency === 'PKR')?.amount.value
              ? formatCurrency(balances.find(b => b.currency === 'PKR')!.amount.value, 'PKR')
              : 'PKR 0.00'}
            icon={Wallet}
            color="purple"
            badge={<Badge variant="purple">Wise</Badge>}
          />
          <StatCard
            title="USD/PKR Rate"
            value={exchangeRate ? exchangeRate.rate.toFixed(2) : '---'}
            icon={TrendingUp}
            color="orange"
            badge={<Badge variant="orange">Live</Badge>}
          />
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="relative overflow-hidden rounded-2xl p-5 liquid-material-regular liquid-lens shadow-glass dark:shadow-dark-glass cursor-pointer"
            onClick={() => { fetchBalance(); fetchExchangeRate(); }}
          >
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue/10 to-purple/10 rounded-2xl flex items-center justify-center">
                <RefreshCw className={`w-6 h-6 text-blue ${loading ? 'animate-spin' : ''}`} />
              </div>
              <span className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary">
                Refresh Balances
              </span>
            </div>
          </motion.div>
        </StatCardGrid>
      </motion.div>

      {/* Fetch Transactions Section */}
      <motion.div variants={itemVariants} className="mt-6">
        <GlassCard>
          <div className="p-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gradient-to-br from-blue/10 to-purple/10 rounded-xl flex items-center justify-center">
                <ArrowDownCircle className="w-5 h-5 text-blue" />
              </div>
              <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Fetch Transactions
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
              <Input
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <Input
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                icon={<Calendar className="w-4 h-4" />}
              />
              <Select
                label="Currency"
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                options={[
                  { value: 'USD', label: 'USD' },
                  { value: 'PKR', label: 'PKR' }
                ]}
              />
              <div className="flex items-end">
                <Button
                  variant="primary"
                  fullWidth
                  icon={<Download className="w-4 h-4" />}
                  onClick={fetchTransactions}
                  disabled={loading}
                  isLoading={loading}
                >
                  Fetch from Wise
                </Button>
              </div>
            </div>

            {/* Transaction Stats */}
            {transactions.length > 0 && (
              <div className="flex items-center justify-between p-4 bg-gradient-to-br from-blue/5 to-purple/5 rounded-xl">
                <div className="flex items-center gap-4">
                  <span className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">
                    Found {transactions.length} transactions
                  </span>
                  <Badge variant="green">
                    {transactions.filter(t => t.type === 'CREDIT').length} incoming
                  </Badge>
                  <Badge variant="red">
                    {transactions.filter(t => t.type === 'DEBIT').length} outgoing
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={selectAllCredits}
                  >
                    Select All Credits
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}
          </div>
        </GlassCard>
      </motion.div>

      {/* Transactions List */}
      {transactions.length > 0 && (
        <motion.div variants={itemVariants} className="mt-6">
          <ContentCard>
            <div className="p-5 border-b border-black/[0.04] dark:border-white/[0.04] flex items-center justify-between">
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
                Wise Transactions
              </h3>
              {selectedTxns.size > 0 && (
                <Button
                  variant="primary"
                  icon={<ArrowRightCircle className="w-4 h-4" />}
                  onClick={importTransactions}
                  disabled={importing}
                  isLoading={importing}
                >
                  Import {selectedTxns.size} Transaction{selectedTxns.size !== 1 ? 's' : ''}
                </Button>
              )}
            </div>

            <motion.div
              className="divide-y divide-black/[0.04] dark:divide-white/[0.04] max-h-[500px] overflow-y-auto"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {transactions.map((txn, index) => (
                <motion.div
                  key={txn.referenceNumber || index}
                  variants={listItemVariants}
                  custom={index}
                  whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  className={`flex items-center gap-4 p-4 transition-colors cursor-pointer ${
                    selectedTxns.has(txn.referenceNumber) ? 'bg-blue/5' : ''
                  }`}
                  onClick={() => txn.type === 'CREDIT' && toggleTransaction(txn.referenceNumber)}
                >
                  {/* Selection checkbox for credits only */}
                  {txn.type === 'CREDIT' && (
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                        selectedTxns.has(txn.referenceNumber)
                          ? 'bg-blue border-blue'
                          : 'border-light-text-quaternary dark:border-dark-text-quaternary'
                      }`}
                    >
                      {selectedTxns.has(txn.referenceNumber) && (
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      )}
                    </motion.div>
                  )}

                  {/* Transaction icon */}
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                    txn.type === 'CREDIT'
                      ? 'bg-gradient-to-br from-green/10 to-green/5'
                      : 'bg-gradient-to-br from-red/10 to-red/5'
                  }`}>
                    {txn.type === 'CREDIT' ? (
                      <ArrowDownCircle className="w-6 h-6 text-green" />
                    ) : (
                      <TrendingUp className="w-6 h-6 text-red rotate-180" />
                    )}
                  </div>

                  {/* Transaction details */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary truncate">
                      {txn.details?.senderName || txn.details?.description || 'Unknown'}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="flex items-center gap-1.5 text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(txn.date)}
                      </span>
                      <Badge variant="gray" size="sm">
                        {txn.referenceNumber}
                      </Badge>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-title3 font-bold ${
                      txn.type === 'CREDIT' ? 'text-green' : 'text-red'
                    }`}>
                      {txn.type === 'CREDIT' ? '+' : '-'}
                      {formatCurrency(Math.abs(txn.amount.value), txn.amount.currency)}
                    </p>
                    {txn.runningBalance && (
                      <p className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
                        Balance: {formatCurrency(txn.runningBalance.value, txn.runningBalance.currency)}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </ContentCard>
        </motion.div>
      )}

      {/* Empty State */}
      {transactions.length === 0 && !loading && (
        <motion.div variants={itemVariants} className="mt-6">
          <ContentCard>
            <div className="text-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue/10 to-purple/10 flex items-center justify-center"
              >
                <Download className="w-10 h-10 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No Transactions Loaded
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
                Select a date range and click "Fetch from Wise" to load transactions
              </p>
            </div>
          </ContentCard>
        </motion.div>
      )}
    </motion.div>
  )
}
